import { Injectable, ApplicationRef, ComponentFactoryResolver, Injector, PLATFORM_ID, Inject, EmbeddedViewRef, ComponentRef, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { MktModalContent } from '../models/mkt-modal-content.model';
import { isPlatformBrowser } from '@angular/common';
import { MktModalComponent } from 'src/app/common/components/mkt-modal/mkt-modal.component';
import { MktContentStorageService } from './mkt-content-storage.service';

@Injectable({
  providedIn: 'root'
})
export class MktModalService implements OnDestroy {
  public defaultContentObject: MktModalContent;
  public modalArray = [];


  constructor(
    private appRef: ApplicationRef,
    public componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    @Inject(PLATFORM_ID) public platformId: string,
    private _mktContentStorageService: MktContentStorageService,
  ) {
    this.defaultContentObject = { isOpen: false };

    this.getDefaultContent();
  }

  ngOnDestroy() {
    if (!isPlatformBrowser(this.platformId)) return;

    // TODO: Destroy any remaining modals
  }

  getDefaultContent() {
    if (!isPlatformBrowser(this.platformId)) return;

    // Set default modal content for external links
    const si = setInterval(() => {
      const getBrandingAttempt = this._mktContentStorageService.get('Branding', 'copy', 'ExternalLinkModal', undefined);
      if (!getBrandingAttempt) return;

      this.defaultContentObject = {
        title: getBrandingAttempt.modalTitle,
        content: getBrandingAttempt.modalContent,
        primaryButtonText: getBrandingAttempt.modalContinueButtonText,
        cancelButtonText: getBrandingAttempt.modalCancelButtonText,
      };

      clearInterval(si);
    }, 250);
  }

  public openModal(event, contentObject: MktModalContent = {}, useDefaults: boolean = true) {
    const modal = this.createModal(event, contentObject, useDefaults);
    return modal;
  }

  private createModal(event, modalContent, useDefaults: boolean = true) {
    if (!isPlatformBrowser(this.platformId)) return;

    document?.body?.classList.add('mkt-modal-open');

    // Create a component reference from the service component
    const modalFactory = this.componentFactoryResolver.resolveComponentFactory(MktModalComponent);
    const modalComponent = modalFactory.create(this.injector);
    const modalInstance = new MktModalInstance(event, modalComponent, this);

    // Push modal instance
    this.modalArray.push(modalInstance);

    // Attach to dom
    const modalElem = this.attachToDom(modalComponent);

    // Assign elem to modal
    modalInstance.addModalElem(modalElem);

    // Set Modal Content
    modalInstance.openModal(modalContent, useDefaults);

    return modalInstance;
  }

  private attachToDom(modalComponent) {
    // Attach component to the appRef so that it's inside the ng component tree
    this.appRef.attachView(modalComponent.hostView);

    // Get DOM element from service component
    let modalDomElem = (modalComponent.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

    // Append DOM element to the body
    document.body.appendChild(modalDomElem);

    return modalDomElem;
  }

  public destroyModal(instance: MktModalInstance) {
    document.body.removeChild(instance.modalElement);
    instance.modalComponentRef.destroy();
    this.modalArray = this.modalArray.filter(item => item != instance);

    if (this.modalArray.length > 0) {
      const component = this.modalArray[this.modalArray.length - 1];
      const element = component.modalElement;
    }
    else {
      document?.body?.classList.remove('mkt-modal-open');
    }
  }

  public addEventListeners(containerElement: HTMLElement, cssSelectors: string[] = ['.speedBump', '.externalLink', '.external-link'], callbackFunction: Function = this.openSpeedBump.bind(this)) {
    // let domList = containerElement.querySelectorAll(".speedBump, .externalLink, .external-link");
    let domList = containerElement.querySelectorAll(cssSelectors.join(', '));
    [].slice.call(domList).forEach((externalLink: HTMLLinkElement) => {
      if (externalLink.getAttribute('data-listenerAdded') === 'true') return;

      externalLink.addEventListener("click", (event) => callbackFunction(event));
      externalLink.addEventListener("keydown", (event) => event.key == "Enter" && event.preventDefault());
      externalLink.addEventListener("keyup", (event) => event.key == "Enter" && callbackFunction(event));

      externalLink.setAttribute('data-listenerAdded', 'true');
    });
  }

  public openSpeedBump(event) {
    event.preventDefault();

    this.openModal(event, {
      url: event.currentTarget.href,
      target: event.currentTarget,
    });
  }

  // private setAriaHidden(component, resetAll: Boolean = false) {
  //   // Loop through all children of body element
  //   const children = document.getElementsByTagName('body')[0].children;
  //   [].slice.call(children).forEach((child: HTMLElement) => {
  //     if (child == component?.location?.nativeElement) return; // Don't toggle modal itself

  //     if (resetAll) {
  //       if (child.getAttribute('data-alwaysHidden') !== 'true') {
  //         child.removeAttribute('aria-hidden');
  //       }

  //       return;
  //     }

  //     if (this.modalArray.length > 0) {
  //       if (this.modalArray.length == 1 && child.getAttribute('aria-hidden') === 'true') {
  //         child.setAttribute('data-alwaysHidden', 'true');
  //       }

  //       child.setAttribute('aria-hidden', 'true');
  //     }
  //     // else {
  //     //   child.removeAttribute('aria-hidden');
  //     // }
  //   });
  // }
}

@Injectable({
  providedIn: 'root'
})
export class MktModalInstance implements OnDestroy {
  private primaryButtonSub: Subscription;
  private closeButtonSub: Subscription;

  public onOpen: Subject<any>;
  public onClose: Subject<any>;
  public onContinue: Subject<any>;
  public modalElement: HTMLElement;

  constructor(
    private event: Event,
    public modalComponentRef: ComponentRef<MktModalComponent>,
    private modalService: MktModalService,
  ) {
    this.onOpen = new Subject();
    this.onClose = new Subject();
    this.onContinue = new Subject();

    if (!isPlatformBrowser(this.modalService.platformId)) return;

    // Subscribe to modal
    const instance = this.modalComponentRef.instance;
    instance.previousActiveElement = event?.currentTarget;
    this.closeButtonSub = instance.onCloseEnd.subscribe(this.handleClose.bind(this));
    this.primaryButtonSub = instance.onContinueEnd.subscribe(this.handleContinue.bind(this));
  }

  ngOnDestroy() {
    if (!isPlatformBrowser(this.modalService.platformId)) return;

    this.primaryButtonSub.unsubscribe();
    this.closeButtonSub.unsubscribe();
  }

  public async openModal(content: any, useDefaults: boolean = true) {
    const defaultContent = this.modalService.defaultContentObject;
    const modal = this.modalComponentRef.instance;

    // Set modal content
    modal.title = content.title;
    modal.primaryButtonText = content.primaryButtonText;
    modal.primaryButtonUrl = content.primaryButtonUrl || content.url;
    modal.primaryButtonTarget = content.target;
    modal.cancelButtonText = content.cancelButtonText;
    modal.showCancelButton = (content.showCancelButton + '' == 'false') ? false : true;
    if (content.content) modal.htmlContent = `<p>${content.content}</p>`;
    if (content.genericTemplateContent) modal.genericTemplateContent = content.genericTemplateContent;
    modal.isModalScrollable = content.isModalScrollable || false;
    // Set defaults values if not overriden
    if (useDefaults) {
      if (!content.title) modal.title = defaultContent.title;
      if (!content.primaryButtonText) modal.primaryButtonText = defaultContent.primaryButtonText;
      if (!content.url) modal.primaryButtonUrl = defaultContent.url;
      if (!content.target) modal.primaryButtonTarget = <"_self" | "_blank">defaultContent.target;
      if (!content.cancelButtonText) modal.cancelButtonText = defaultContent.cancelButtonText;
      if (!content.content) modal.htmlContent = `<p>${defaultContent.content}</p>`;
    }

    // Add speedbump
    const instance = setTimeout(() => {
      this.modalService.addEventListeners(modal?.cdsModalBody?.nativeElement);
      clearTimeout(instance);
    }, 0);

    this.onOpen.next(modal);
  }

  public addModalElem(elem) {
    this.modalElement = elem;
  }

  private handleClose() {
    this.onClose.next(null);
    this.modalService.destroyModal(this);
  }

  private handleContinue() {
    this.onContinue.next(null);
    this.modalService.destroyModal(this);
  }
}

